import React from 'react';
import { Page, Text, View, Document, Font } from '@react-pdf/renderer';
import notoSansArabic from 'fonts/NotoSansArabic-Regular.ttf';
import RobotoBold from 'fonts/Roboto-Medium.ttf';
import RobotoRegular from 'fonts/Roboto-Regular.ttf';
import RobotoItalic from 'fonts/Roboto-Italic.ttf';
import RobotoBoldItalic from 'fonts/Roboto-BoldItalic.ttf';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/Sheets/messages';
import { styles } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import { localTranslate, translate } from 'containers/services/translate';
import { IntlShape } from 'react-intl';
import { Listings } from 'containers/AppContainer/typings';
import {
  AssignedLawyer,
  RcCorporeal,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/typings';
import { ARABIC_LOCAL } from 'containers/LanguageProvider/constants';
import moment from 'moment-timezone';
import { reverseText } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/utils';
import {
  getCNRADestination,
  numberWithCommas,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import { get } from 'lodash';
import { getBeneficiaries } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/Sheets/utils';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { NONE } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { RECIPIENT } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { ClaimRecapType } from 'typings/claimTypes';

Font.register({
  family: 'NotooArabic',
  src: notoSansArabic,
});
Font.register({
  family: 'Roboto',
  src: RobotoRegular,
  fontWeight: 'normal',
  fonts: [
    {
      src: RobotoBold,
      fontWeight: 'bold',
    },
    {
      src: RobotoRegular,
      fontWeight: 'normal',
    },
    {
      src: RobotoItalic,
      fontWeight: 'normal',
      fontStyle: 'italic',
    },
    {
      src: RobotoBoldItalic,
      fontWeight: 'bold',
      fontStyle: 'italic',
    },
  ],
});
export interface CnraSheetProps {
  intl: IntlShape;
  rcCorpo: RcCorporeal;
  claimRecap: ClaimRecapType;
  judgementOnMerits: any;
  sagisClaim: any;
  corpoEncryptedDocumentsResult: any;
  listings: Listings;
  assignedLawyers: AssignedLawyer[];
  encryptedDocumentsOfAdults: any;
  encryptedDocumentsOfMinors: any;
  provisionInfo: any;
  setProvisionInfo: Function;
  hasJom: boolean;
  corpoRegulations: any;
  regulations: any;
  lossDate: any;
  policyInfo: any;
}
export const CnraSheet: React.FC<CnraSheetProps> = (props): JSX.Element => {
  const { totalAmount = 0 } = props.encryptedDocumentsOfMinors;
  const { cityName } = getCNRADestination(props.listings);

  const isAppeal = get(
    get(props.rcCorpo, 'regulationInfo', {}),
    [REGULATIONS.IS_APPEAL],
    false,
  );
  return (
    <Document>
      <Page style={[styles.page]}>
        <View style={[styles.section]}>
          <View style={[styles.column, { paddingRight: 10 }]}>
            <View
              style={[
                {
                  alignItems: 'center',
                  margin: '15px 0px 15px 15px',
                },
              ]}
            >
              <Text
                style={[
                  styles.item,
                  styles.boldText,
                  styles.withUnderline,
                  { fontSize: 14 },
                ]}
              >
                {translate(messages.cnraTitle, props)}
              </Text>
            </View>
            <View style={[styles.withBorder]}>
              <View style={[styles.arabicElements, styles.rowColumn]}>
                <Text style={[styles.cnraArabicText, styles.withUnderline]}>
                  {localTranslate(messages.cdraSheetTheme, ARABIC_LOCAL)}
                </Text>
              </View>
              <View style={[styles.arabicElements, styles.rowColumn]}>
                <View
                  style={[
                    styles.rowColumn,
                    styles.arabicItem,
                    { width: '15%' },
                  ]}
                >
                  <Text style={[styles.cnraArabicText]}>
                    {localTranslate(
                      messages.cdraSheetRulingNumber,
                      ARABIC_LOCAL,
                    )}
                  </Text>
                </View>
                <View style={[styles.rowColumn, styles.arabicItem]}>
                  <Text style={[styles.cnraArabicText]}>
                    {localTranslate(messages.issuedOn, ARABIC_LOCAL)}
                  </Text>
                  <Text style={[styles.item]}>
                    {!isAppeal &&
                    get(
                      props.rcCorpo.regulationInfo,
                      REGULATIONS.JUDGEMENT_DATE,
                    ) !== NONE
                      ? get(
                          props.rcCorpo.regulationInfo,
                          REGULATIONS.JUDGEMENT_DATE,
                        )
                      : (get(
                          props.rcCorpo.regulationInfo,
                          REGULATIONS.JUDGEMENT_DATE,
                          '',
                        ) === NONE ||
                          isAppeal) &&
                        get(
                          props.rcCorpo.regulationInfo,
                          REGULATIONS.JUDGEMENT_DATE_JAF,
                        )}
                  </Text>
                </View>
                <View
                  style={[
                    styles.rowColumn,
                    styles.arabicItem,
                    { width: '35%' },
                  ]}
                >
                  <Text style={[styles.cnraArabicText]}>
                    {localTranslate(messages.firstInstanceCourt, ARABIC_LOCAL)}
                  </Text>
                  {!isAppeal && (
                    <Text style={[styles.item]}>
                      {translate(messages.firstInstance, props)}
                    </Text>
                  )}
                </View>
              </View>
              <View style={[styles.rowColumn]}>
                <View
                  style={[
                    styles.arabicElements,
                    styles.rowColumn,
                    { justifyContent: 'flex-start' },
                  ]}
                >
                  <View style={styles.checkbox} />
                  <Text style={[styles.cnraArabicText]}>
                    {localTranslate(
                      messages.friendlyImplementation,
                      ARABIC_LOCAL,
                    )}
                  </Text>
                </View>
              </View>
              <View style={[styles.arabicElements, styles.rowColumn]}>
                <View
                  style={[
                    styles.rowColumn,
                    styles.arabicItem,
                    { width: '15%' },
                  ]}
                >
                  <Text style={[styles.cnraArabicText]}>
                    {localTranslate(messages.resolutionNumber, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View style={[styles.rowColumn, styles.arabicItem]}>
                  <Text style={[styles.cnraArabicText]}>
                    {localTranslate(messages.issuedOn, ARABIC_LOCAL)}
                  </Text>
                  {!!isAppeal && (
                    <Text style={[styles.item]}>
                      {get(
                        props.rcCorpo.regulationInfo,
                        REGULATIONS.JUDGEMENT_DATE,
                      )}
                    </Text>
                  )}
                </View>
                <View
                  style={[
                    styles.rowColumn,
                    styles.arabicItem,
                    { width: '35%' },
                  ]}
                >
                  <Text style={[styles.cnraArabicText]}>
                    {localTranslate(messages.fromAppeal, ARABIC_LOCAL)}
                  </Text>
                  {isAppeal && (
                    <Text style={[styles.item]}>
                      {translate(messages.appeal, props)}
                    </Text>
                  )}
                </View>
              </View>
              <View style={[styles.arabicElements, styles.rowColumn]}>
                <View
                  style={[
                    styles.arabicElements,
                    styles.rowColumn,
                    {
                      justifyContent: 'flex-start',
                    },
                  ]}
                >
                  <View style={styles.checkbox} />
                  <Text style={[styles.cnraArabicText]}>
                    {`${localTranslate(
                      messages.reconciliationChapter,
                      ARABIC_LOCAL,
                    )} ${reverseText('18')} ${localTranslate(
                      messages.reconciliationChapterFrom,
                      ARABIC_LOCAL,
                    )} `}
                  </Text>
                  <Text style={{ textAlign: 'right' }}>
                    {localTranslate(
                      messages.reconciliationChapterDate,
                      ARABIC_LOCAL,
                    )}
                  </Text>
                </View>
              </View>
            </View>
            <View
              style={[
                {
                  alignItems: 'flex-end',
                  margin: '10px 0px 10px 10px',
                },
              ]}
            >
              <Text style={[styles.cnraArabicText, styles.withUnderline]}>
                {localTranslate(messages.fileInformation, ARABIC_LOCAL)}
              </Text>
            </View>
            <View style={[styles.table, { margin: 0 }]}>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.lawyerName, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.uppercase]}>
                    {get(props.rcCorpo, 'regulationInfo.civilPartyLawyer')}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.arabicText]}>
                    {localTranslate(messages.board, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.uppercase]}>
                    {`${cityName}-${RECIPIENT.CNRA}`}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.attornyAdress, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, { alignSelf: 'flex-end' }]}>
                    {translate(messages.dots, props)}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.lawyerReference, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, { alignSelf: 'flex-end' }]}>
                    {translate(messages.dots, props)}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.beneficiaryName, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.uppercase]}>
                    {getBeneficiaries(
                      props.rcCorpo,
                      props.sagisClaim,
                      props.corpoEncryptedDocumentsResult,
                      get(props.listings, 'PaymentStatus', []),
                      true,
                    )}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.finalAmount, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item]}>
                    {numberWithCommas(String(totalAmount.toFixed(2)))}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.lossDate, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item]}>
                    {moment(props.lossDate).format('DD/MM/YYYY')}
                  </Text>
                </View>
              </View>

              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[styles.tableCol, styles.rowColumn, styles.tableWidth]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.accidentType, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.uppercase]}>
                    {translate(messages.traffic, props)}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.registrationNumber, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item]}>
                    {`${get(
                      props.policyInfo,
                      'sagis.policy.notStandardizedRegistrationNumber',
                      '',
                    )} - ${get(
                      props.policyInfo,
                      'sagis.policy.vehicleMark',
                      '',
                    )}`}
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.insuredName, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item]}>
                    <Text style={[styles.item]}>
                      {`${get(
                        props.policyInfo,
                        'sagis.policy.clientFirstName',
                      )} ${get(
                        props.policyInfo,
                        'sagis.policy.clientLastName',
                      )}`}
                    </Text>
                  </Text>
                </View>
              </View>
              <View style={[styles.tableRow, styles.tableElem]}>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item, styles.cnraArabicText]}>
                    {localTranslate(messages.policyNumber, ARABIC_LOCAL)}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    styles.row,
                    styles.tableWidth,
                    styles.tableElem,
                  ]}
                >
                  <Text style={[styles.item]}>
                    {get(props.sagisClaim, 'code')}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default CnraSheet;
